import { useEffect } from "react";
import { AuthType } from "./types";
import { ZEPHR_DIALOG_MARKER, ZEPHR_FEATURES } from "lib/zephr/constants";

type UseZephrFormProps = {
  section?: Section;
  selector?: string;
  initiallyRequiredFields?: string[];
  validateOnLoad?: boolean;
  onUpdate?: (sectionName: string, fields: Fields) => void;
  onLoad?: (fields: Fields) => void;
  canSubmit?: (fields: Fields) => boolean;
  onSuccess?: (authType: AuthType, values: Values) => void;
};

type Fields = { [key: string]: HTMLFormElement };
type Values = { [key: string]: string | boolean };

/* eslint-disable no-unused-vars */
export enum Section {
  CREATE_YOUR_ACCOUNT = "create-your-account",
  UPDATE_MISSING_INFO = "update-missing-info",
  COMPLETE_YOUR_PROFILE = "complete-your-profile",
  COMPLETE_PROFILE_OPTIONAL = "complete-profile-optional",
  INVOICE_DETAILS = "invoice-details",
  SELECT_PLAN = "select-plan",
}

export enum FormEvent {
  INPUT_CHANGE = "zephr_form_input_change",
  LOGIN = "zephr_form_login",
  REGISTER = "zephr_form_register",
  SECTION_CHANGE = "zephr_form_section_change",
}

const getValue = (field?: HTMLFormElement) => {
  return field?.type === "checkbox" ? field?.checked : field?.value;
};

const SECTION_BY_FEATURE: [string, Section][] = [
  [ZEPHR_FEATURES.COMPLETE_PROFILE_OPTIONAL_FORM, Section.COMPLETE_PROFILE_OPTIONAL],
];

export const getSection = (form: HTMLFormElement) => {
  const feature = form.closest(".zephr-feature");

  if (feature) {
    const match = SECTION_BY_FEATURE.find(([name]) =>
      feature.classList.contains(name)
    );

    if (match) {
      return match[1];
    }
  }

  if (form.querySelector('input[name="form_job_title"]')) {
    return Section.UPDATE_MISSING_INFO;
  }

  if (form.querySelector('input[name="first_name"]')) {
    return Section.CREATE_YOUR_ACCOUNT;
  }

  if (form.querySelector('input[name="would-like-event-newsletter"]')) {
    return Section.COMPLETE_YOUR_PROFILE;
  }

  if (form.querySelector('input[name="self_serve_need_an_invoice"]')) {
    return Section.INVOICE_DETAILS;
  }

  if (form.querySelector(".zephr-radio-payment-options-page")) {
    return Section.SELECT_PLAN;
  }
};

const getFields = (form: HTMLFormElement) => {
  const elements = [
    ...Array.from(form.querySelectorAll<HTMLFormElement>("input, select")),
    ...Array.from(form.querySelectorAll<HTMLFormElement>("input:checked")),
  ];

  return elements.reduce<Fields>(
    (fields, field) => ({ ...fields, [field.name]: field }),
    {}
  );
};

const setupSignMeUpToAllNewsletters = (form: HTMLFormElement) => {
  const allNewsletters = form.querySelector<HTMLInputElement>(
    'input[id="sign-me-up-to-all-newsletters"]'
  );

  if (!allNewsletters) {
    return;
  }

  allNewsletters.classList.add(
    ...Array.from(form.querySelector(".zephr-form-checkmark")?.classList ?? [])
  );

  const newslettersCheckboxes = Array.from(
    form.querySelectorAll<HTMLInputElement>(
      'input[type="checkbox"][name]:not([name="would-like-event-newsletter"])'
    )
  );

  allNewsletters.addEventListener("input", () => {
    allNewsletters.classList.toggle("checked", allNewsletters.checked);

    for (const checkbox of newslettersCheckboxes) {
      setTimeout(() => {
        checkbox.checked = allNewsletters.checked;
        checkbox.dispatchEvent(new Event("input"));
      }, 10);
    }
  });

  form.addEventListener("input", (event) => {
    const target = event.target as HTMLElement;

    if (!target.matches(".zephr-form-checkbox")) {
      return;
    }

    allNewsletters.checked = newslettersCheckboxes.reduce(
      (checked, checkbox) => checked && checkbox.checked,
      true
    );
    allNewsletters.classList.toggle("checked", allNewsletters.checked);
  });
};

const DEFAULT_FORM_SELECTOR = ".zephr-form-container form";

export const findZephrForm = (selector?: string): HTMLFormElement | null => {
  if (selector) {
    return document.querySelector<HTMLFormElement>(selector);
  }

  return (
    Array.from(
      document.querySelectorAll<HTMLFormElement>(DEFAULT_FORM_SELECTOR)
    ).find((form) => !form.closest(`[${ZEPHR_DIALOG_MARKER}]`)) ?? null
  );
};

export const useZephrForm = ({
  section: forcedSection,
  selector,
  initiallyRequiredFields,
  validateOnLoad = false,
  onUpdate,
  onSuccess,
  onLoad,
  canSubmit,
}: UseZephrFormProps) => {
  useEffect(() => {
    const form = findZephrForm(selector);

    if (!form) {
      return;
    }

    const isSubmitDisabled = (fields: Fields) =>
      !form.checkValidity() || (canSubmit ? !canSubmit(fields) : false);

    const applyInitiallyRequired = (fields: Fields) => {
      initiallyRequiredFields &&
        initiallyRequiredFields.forEach((fieldName) => {
          fields[fieldName] && (fields[fieldName].required = true);
        });
    };

    const syncSubmitButton = (fields: Fields) => {
      const button = form.querySelector<HTMLButtonElement>(
        'button[type="submit"]'
      );

      if (button) {
        button.disabled = isSubmitDisabled(fields);
      }
    };

    const syncsOnLoad = validateOnLoad || Boolean(canSubmit);
    const initialFields = getFields(form);

    applyInitiallyRequired(initialFields);
    onLoad && onLoad(initialFields);
    form.dataset["section"] = forcedSection ?? getSection(form);
    syncsOnLoad && syncSubmitButton(getFields(form));

    const values: Values = {};

    form.addEventListener(
      "click",
      (event) => {
        if (!(event.target instanceof HTMLElement)) {
          return;
        }

        if (!event.target.matches('button[type="submit"]')) {
          return;
        }

        Object.values(getFields(form)).forEach((field) => {
          if (!field.checkVisibility()) {
            field.value = "";
            field.dispatchEvent(new Event("input"));
          }
        });
      },
      true
    );

    const callback = (event: MessageEvent) => {
      const zephrEvent = event.data["zephr-form-datalayer-event"];

      if (!zephrEvent) {
        return;
      }

      const fields = getFields(form);
      const section = forcedSection ?? getSection(form);

      if (zephrEvent.event === FormEvent.SECTION_CHANGE) {
        form.dataset["section"] = section;

        if (section === Section.COMPLETE_YOUR_PROFILE) {
          setupSignMeUpToAllNewsletters(form);
        }

        applyInitiallyRequired(fields);
        onLoad && onLoad(fields);
        onUpdate && section && onUpdate(section, fields);
        syncsOnLoad && syncSubmitButton(fields);
      }

      if (zephrEvent.event === FormEvent.INPUT_CHANGE) {
        values[zephrEvent.field] = getValue(fields[zephrEvent.field]);

        if (section) {
          onUpdate && onUpdate(section, fields);
          syncSubmitButton(fields);
        }
      }

      if (!zephrEvent.success) {
        return;
      }

      if ([FormEvent.REGISTER, FormEvent.LOGIN].includes(zephrEvent.event)) {
        onSuccess?.(
          zephrEvent.event === FormEvent.REGISTER
            ? AuthType.REGISTRATION
            : AuthType.LOGIN,
          values
        );
      }
    };

    window.addEventListener("message", callback);

    return () => window.removeEventListener("message", callback);
  }, [
    forcedSection,
    selector,
    initiallyRequiredFields,
    validateOnLoad,
    onUpdate,
    onSuccess,
    onLoad,
    canSubmit,
  ]);
};
